import { useState, useMemo } from "react";
import { SKU } from "@/data/mockData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { formatCurrency } from "@/lib/utils";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { 
  Package, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  DollarSign,
  Settings,
  Filter
} from "lucide-react";

interface SKUStatsProps {
  skus: SKU[];
}

export const SKUStats = ({ skus }: SKUStatsProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("30d");
  const [chartType, setChartType] = useState<"bar" | "pie" | "line">("bar");

  // Filter SKUs based on selection
  const filteredSKUs = useMemo(() => {
    return selectedCategory === "all" 
      ? skus 
      : skus.filter(sku => sku.category === selectedCategory);
  }, [skus, selectedCategory]);

  // Get unique categories
  const categories = useMemo(() => {
    return Array.from(new Set(skus.map(sku => sku.category)));
  }, [skus]);

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalSKUs = filteredSKUs.length;
    const activeSKUs = filteredSKUs.filter(sku => sku.isActive).length;
    const lowStockSKUs = filteredSKUs.filter(sku => sku.currentStock <= sku.reorderLevel).length;
    const outOfStockSKUs = filteredSKUs.filter(sku => sku.currentStock <= 0).length;
    const totalStockValue = filteredSKUs.reduce((sum, sku) => {
      const price = sku.linkedVendors[0]?.defaultPrice || 0;
      return sum + (sku.currentStock * price);
    }, 0);
    const averageStockLevel = totalSKUs > 0 
      ? filteredSKUs.reduce((sum, sku) => sum + sku.currentStock, 0) / totalSKUs 
      : 0;

    return {
      totalSKUs,
      activeSKUs,
      lowStockSKUs,
      outOfStockSKUs,
      totalStockValue,
      averageStockLevel
    };
  }, [filteredSKUs]);

  // Category distribution data
  const categoryData = useMemo(() => {
    const categoryCount = categories.reduce((acc, category) => {
      acc[category] = skus.filter(sku => sku.category === category).length;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(categoryCount).map(([name, value]) => ({
      name,
      value,
      percentage: Number(((value / skus.length) * 100).toFixed(1))
    }));
  }, [skus, categories]);

  // Stock status distribution
  const stockStatusData = useMemo(() => {
    const outOfStock = filteredSKUs.filter(sku => sku.currentStock <= 0).length;
    const lowStock = filteredSKUs.filter(sku => sku.currentStock > 0 && sku.currentStock <= sku.reorderLevel).length;
    const warning = filteredSKUs.filter(sku => sku.currentStock > sku.reorderLevel && sku.currentStock <= sku.reorderLevel * 1.5).length;
    const good = filteredSKUs.filter(sku => sku.currentStock > sku.reorderLevel * 1.5).length;

    return [
      { name: 'Out of Stock', value: outOfStock, color: '#ef4444' },
      { name: 'Low Stock', value: lowStock, color: '#f97316' },
      { name: 'Warning', value: warning, color: '#eab308' },
      { name: 'Good', value: good, color: '#22c55e' }
    ].filter(item => item.value > 0);
  }, [filteredSKUs]);

  // Top SKUs by value
  const topSKUsByValue = useMemo(() => {
    return filteredSKUs
      .map(sku => ({
        ...sku,
        totalValue: sku.currentStock * (sku.linkedVendors[0]?.defaultPrice || 0)
      }))
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 10);
  }, [filteredSKUs]);

  // Stock level distribution
  const stockLevelData = useMemo(() => {
    const ranges = [
      { name: '0-100', min: 0, max: 100 },
      { name: '101-500', min: 101, max: 500 },
      { name: '501-1000', min: 501, max: 1000 },
      { name: '1001-5000', min: 1001, max: 5000 },
      { name: '5000+', min: 5001, max: Infinity }
    ];

    return ranges.map(range => ({
      name: range.name,
      count: filteredSKUs.filter(sku => 
        sku.currentStock >= range.min && sku.currentStock <= range.max
      ).length
    })).filter(item => item.count > 0);
  }, [filteredSKUs]);

  // Price range distribution
  const priceRangeData = useMemo(() => {
    const ranges = [
      { name: '₹0-1', min: 0, max: 1 },
      { name: '₹1-10', min: 1, max: 10 },
      { name: '₹10-50', min: 10, max: 50 },
      { name: '₹50-100', min: 50, max: 100 },
      { name: '₹100+', min: 100, max: Infinity }
    ];

    return ranges.map(range => ({
      name: range.name,
      count: filteredSKUs.filter(sku => {
        const price = sku.linkedVendors[0]?.defaultPrice || 0;
        return price >= range.min && price <= range.max;
      }).length
    })).filter(item => item.count > 0);
  }, [filteredSKUs]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">SKU Analytics</h2>
          <p className="text-muted-foreground">Comprehensive insights into your inventory</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={chartType} onValueChange={(value: "bar" | "pie" | "line") => setChartType(value)}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bar">Bar Chart</SelectItem>
              <SelectItem value="pie">Pie Chart</SelectItem>
              <SelectItem value="line">Line Chart</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-primary" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total SKUs</p>
                <p className="text-2xl font-bold">{overallStats.totalSKUs.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  {overallStats.activeSKUs} active
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Stock Value</p>
                <p className="text-2xl font-bold">{formatCurrency(overallStats.totalStockValue)}</p>
                <p className="text-xs text-muted-foreground">
                  Total inventory value
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Low Stock</p>
                <p className="text-2xl font-bold">{overallStats.lowStockSKUs}</p>
                <p className="text-xs text-muted-foreground">
                  Require attention
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Avg Stock</p>
                <p className="text-2xl font-bold">{Math.round(overallStats.averageStockLevel).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  Per SKU
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5" />
              Category Distribution
            </CardTitle>
            <CardDescription>SKU count by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Stock Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Stock Status
            </CardTitle>
            <CardDescription>Current stock level distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stockStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stockStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Level Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Stock Level Distribution
          </CardTitle>
          <CardDescription>Number of SKUs in each stock level range</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stockLevelData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top SKUs by Value */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top SKUs by Stock Value
          </CardTitle>
          <CardDescription>Highest value inventory items</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topSKUsByValue.slice(0, 8).map((sku, index) => (
              <div key={sku.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{sku.name}</div>
                    <div className="text-sm text-muted-foreground">{sku.skuCode}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{formatCurrency(sku.totalValue)}</div>
                  <div className="text-sm text-muted-foreground">
                    {sku.currentStock.toLocaleString()} × {formatCurrency(sku.linkedVendors[0]?.defaultPrice || 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stock Alerts */}
      {(overallStats.lowStockSKUs > 0 || overallStats.outOfStockSKUs > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Stock Alerts
            </CardTitle>
            <CardDescription>SKUs requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredSKUs
                .filter(sku => sku.currentStock <= sku.reorderLevel)
                .sort((a, b) => a.currentStock - b.currentStock)
                .slice(0, 10)
                .map((sku) => {
                  const isOutOfStock = sku.currentStock <= 0;
                  const stockPercentage = sku.maxStock > 0 ? (sku.currentStock / sku.maxStock) * 100 : 0;
                  
                  return (
                    <div key={sku.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {isOutOfStock ? (
                          <XCircle className="h-5 w-5 text-red-500" />
                        ) : (
                          <AlertTriangle className="h-5 w-5 text-orange-500" />
                        )}
                        <div>
                          <div className="font-medium">{sku.name}</div>
                          <div className="text-sm text-muted-foreground">{sku.skuCode}</div>
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <Badge variant={isOutOfStock ? "destructive" : "secondary"}>
                          {isOutOfStock ? "Out of Stock" : "Low Stock"}
                        </Badge>
                        <div className="text-sm">
                          {sku.currentStock.toLocaleString()} / {sku.reorderLevel.toLocaleString()} {sku.unit}
                        </div>
                        <Progress value={stockPercentage} className="w-24 h-2" />
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {categoryData.slice(0, 5).map((category, index) => (
                <div key={category.name} className="flex items-center justify-between">
                  <span className="text-sm">{category.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                    <span className="text-sm font-medium">{category.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Price Ranges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priceRangeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Package className="h-4 w-4 mr-2" />
              Export SKU Report
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Generate Reorder Report
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <BarChart3 className="h-4 w-4 mr-2" />
              Stock Analysis
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <DollarSign className="h-4 w-4 mr-2" />
              Value Analysis
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

